import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Skeleton } from '@mui/material';
import moment from 'moment';

import RecipientActionWrapper from '../RecipientActionWrapper';
import useRequiredActions from '../../hooks/useRequiredActions';
import PageTitle from '../PageTitle/PageTitle';
import { GIFT_FLOW_INFORMATION_ID } from '../../../../constants/giftFlow.constants';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import MeetingInfoCard from '../MeetingInfoCard/MeetingInfoCard';
import FreeMeetingInfoCard from '../MeetingInfoCard/FreeMeetingInfoCard';
import useMeetingInfo from '../../hooks/useMeetingInfo';
import { FULL_DATE_FORMAT } from '../../../../constants/dates.constants';
import { getSenderState } from '../../../../store/users/users.selectors';

import RequiredActionsForm from './RequiredActionsForm/RequiredActionsForm';

const RequiredInfoPage = (): JSX.Element => {
  const { setActiveStatus, goNextStep, goPreviousStep } = useGiftStatuses();

  const sender = useSelector(getSenderState);

  const { raLoaded, raLoading, requiredActions, errors, submitProspects } = useRequiredActions(goNextStep);

  const showSkeleton = !raLoaded && raLoading;

  const titleContent = <PageTitle title="Please complete the following" />;

  const { isSingleSlotFlow, isMultiSlotFlow, timezoneName, meetingSlots } = useMeetingInfo();

  const handleSubmitProspect = useCallback(
    prospectValues => {
      const slot = isSingleSlotFlow || isMultiSlotFlow ? meetingSlots?.[0] : null;
      const date = slot ? moment(slot.startAt).format(`${FULL_DATE_FORMAT} Z`) : '';
      submitProspects({ date, ...prospectValues });
    },
    [submitProspects, isSingleSlotFlow, isMultiSlotFlow, meetingSlots],
  );

  usePageLoadEvent('Require Info Page - Opened');

  useEffect(() => {
    setActiveStatus(GIFT_FLOW_INFORMATION_ID);
  }, [setActiveStatus]);

  return (
    <RecipientActionWrapper titleContent={titleContent}>
      <Box width={[320, 400]} mb={5}>
        {isSingleSlotFlow && meetingSlots && sender && (
          <Box mt={4}>
            <MeetingInfoCard
              sender={sender}
              slot={meetingSlots[0]}
              timezoneName={timezoneName}
              onAction={goPreviousStep}
            />
          </Box>
        )}
        {isMultiSlotFlow && meetingSlots && sender && (
          <Box mt={4}>
            <FreeMeetingInfoCard
              sender={sender}
              slots={meetingSlots}
              timezoneName={timezoneName}
              onAction={goPreviousStep}
            />
          </Box>
        )}
        {showSkeleton ? (
          <>
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={150} />
          </>
        ) : (
          <RequiredActionsForm
            isLoading={raLoading}
            requiredActions={requiredActions}
            errors={errors}
            onSubmit={handleSubmitProspect}
          />
        )}
      </Box>
    </RecipientActionWrapper>
  );
};

export default RequiredInfoPage;
